import { useEffect, useState } from "react";
import { FolderKanban, CheckSquare, Clock3, Trophy, Plus } from "lucide-react";
import tr from "@/i18n/tr";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import StatCard from "@/components/dashboard/StatCard";
import RevenueMiniChart from "@/components/dashboard/RevenueMiniChart";
import DistributionChart from "@/components/dashboard/DistributionChart";
import UpcomingDeliveries from "@/components/dashboard/UpcomingDeliveries";
import MiniCalendar from "@/components/dashboard/MiniCalendar";
import ActivityFeed from "@/components/dashboard/ActivityFeed";
import QuickActions from "@/components/dashboard/QuickActions";

const TR_MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const TR_DAYS   = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];

function formatToday() {
    const d = new Date();
    return `${d.getDate()} ${TR_MONTHS[d.getMonth()]} ${d.getFullYear()}, ${TR_DAYS[d.getDay()]}`;
}

function greetingFor() {
    const h = new Date().getHours();
    if (h < 12) return tr.dashboard.greetings.morning;
    if (h < 18) return tr.dashboard.greetings.day;
    return tr.dashboard.greetings.evening;
}

export default function DashboardPage() {
    const { user } = useAuth();
    const firstName = user?.full_name?.split(" ")[0] || "";
    const FINANCE_ROLES = new Set(["super_admin", "admin", "agency_owner", "finance", "accountant"]);
    const canSeeFinance = user && FINANCE_ROLES.has(user.role);

    const [stats, setStats] = useState({
        active_projects: 0, open_tasks: 0, pending_approvals: 0, done_tasks: 0,
        monthly_revenue: 0, monthly_expense: 0, monthly_profit: 0,
    });

    useEffect(() => {
        (async () => {
            try {
                const now = new Date();
                const [projects, tasksAll, tasksDone] = await Promise.all([
                    api.get("/projects", { params: { status: "active", page_size: 1 } }).catch(() => null),
                    api.get("/tasks", { params: { page_size: 1 } }).catch(() => null),
                    api.get("/tasks", { params: { status: "done", page_size: 1 } }).catch(() => null),
                ]);
                const finance = canSeeFinance
                    ? await api.get("/reports/monthly", { params: { year: now.getFullYear(), month: now.getMonth() + 1 } }).catch(() => null)
                    : null;
                const financeDash = canSeeFinance
                    ? await api.get("/finance/dashboard").catch(() => null)
                    : null;
                setStats({
                    active_projects: projects?.data?.total || 0,
                    open_tasks: tasksAll?.data?.total || 0,
                    done_tasks: tasksDone?.data?.total || 0,
                    pending_approvals: financeDash?.data?.pending_expenses?.count || 0,
                    monthly_revenue: finance?.data?.total?.revenue || 0,
                    monthly_expense: finance?.data?.total?.expenses || 0,
                    monthly_profit: finance?.data?.total?.profit || 0,
                });
            } catch { /* silent */ }
        })();
    }, [canSeeFinance]);

    const fmtMoney = (v) => { try { return new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(v || 0); } catch { return `₺${v || 0}`; } };

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-[26px] md:text-[28px] font-bold tracking-tight leading-tight">
                        {greetingFor()}, {firstName || "Kullanıcı"}
                        <span className="inline-block ml-2 align-middle">👋</span>
                    </h1>
                    <p className="text-[13.5px] text-muted-foreground mt-1">
                        {tr.dashboard.todayIs}, {formatToday()}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => window.location.href = "/tasks"}
                    data-testid="dashboard-quick-action"
                    className="btn-brand h-11"
                >
                    <Plus className="h-4 w-4" />
                    {tr.common.quickAction}
                </button>
            </div>

            {/* Row 1: Stat cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label={tr.dashboard.stats.activeProjects} value={String(stats.active_projects)} caption={tr.dashboard.stats.comparedLastMonth} Icon={FolderKanban} accent="brand" testId="stat-active-projects" />
                <StatCard label={tr.dashboard.stats.todayTasks} value={String(stats.open_tasks)} caption="Toplam açık görev" Icon={CheckSquare} accent="info" testId="stat-today-tasks" />
                <StatCard label={tr.dashboard.stats.pendingApprovals} value={String(stats.pending_approvals)} caption="Onay bekleyen fişler" Icon={Clock3} accent="warning" testId="stat-pending-approvals" />
                <StatCard label="Tamamlanan Görevler" value={String(stats.done_tasks)} caption={tr.dashboard.stats.completedThisMonth} Icon={Trophy} accent="success" testId="stat-completed-tasks" />
            </div>

            {/* Row 2: Revenue/Expense/Profit + Distribution — sadece Finans yetkilileri */}
            {canSeeFinance && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-finance-row">
                <div className="stat-card" data-testid="stat-monthly-revenue">
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="text-[12.5px] font-medium text-muted-foreground">{tr.dashboard.stats.monthlyRevenue}</div>
                            <div className="mt-3"><span className="text-[26px] font-bold tracking-tight leading-none">{fmtMoney(stats.monthly_revenue)}</span></div>
                            <div className="mt-1 text-[11.5px] text-muted-foreground">Bu ay</div>
                        </div>
                    </div>
                    <RevenueMiniChart variant="income" />
                </div>
                <div className="stat-card" data-testid="stat-monthly-expense">
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="text-[12.5px] font-medium text-muted-foreground">{tr.dashboard.stats.monthlyExpense}</div>
                            <div className="mt-3"><span className="text-[26px] font-bold tracking-tight leading-none">{fmtMoney(stats.monthly_expense)}</span></div>
                            <div className="mt-1 text-[11.5px] text-muted-foreground">Bu ay</div>
                        </div>
                    </div>
                    <RevenueMiniChart variant="expense" />
                </div>
                <div className="stat-card" data-testid="stat-monthly-profit">
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="text-[12.5px] font-medium text-muted-foreground">{tr.dashboard.stats.monthlyProfit}</div>
                            <div className="mt-3"><span className="text-[26px] font-bold tracking-tight leading-none">{fmtMoney(stats.monthly_profit)}</span></div>
                            <div className="mt-1 text-[11.5px] text-muted-foreground">Bu ay</div>
                        </div>
                    </div>
                    <RevenueMiniChart variant="profit" />
                </div>
                <DistributionChart />
            </div>
            )}

            {/* Row 3: Upcoming + Calendar + Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <UpcomingDeliveries />
                <MiniCalendar />
                <ActivityFeed />
            </div>

            {/* Row 4: Quick Actions */}
            <QuickActions />
        </div>
    );
}
